import { AfterViewInit, Component, Input } from '@angular/core';

type MilestoneState = 'done' | 'overdue' | 'current' | 'upcoming';

@Component({
  selector: 'app-milestone',
  templateUrl: './milestone.component.html',
  styleUrls: ['./milestone.component.css']
})
export class MilestoneComponent implements AfterViewInit {

  @Input() lessons: Array<any>;
  @Input() enrolledDate: any;
  @Input() activateLessonDeadlineNotification: boolean;

  ngAfterViewInit(): void {
    this.srcrollToLastCompletedLesson();
  }

  /**
   * Four states, not two. Originally anything incomplete was drawn red, so a student
   * who had simply not started yet saw a row of red dots reading as failure.
   *
   * 'current' marks where the student actually is — the first lesson they have not
   * finished. Overdue is checked first: if that lesson is already past its deadline,
   * that is the more useful thing to say about it.
   */
  state(milestone: any): MilestoneState {
    if (milestone?.completed) {
      return 'done';
    }
    if (this.deadlinePassed(milestone)) {
      return 'overdue';
    }
    return milestone === this.currentMilestone ? 'current' : 'upcoming';
  }

  /** The first lesson not yet completed. Null once every lesson is done. */
  get currentMilestone(): any {
    return this.lessons?.find(l => !l.completed) ?? null;
  }

  /**
   * A connector is solid green when the lesson on its left is finished, and dashed
   * grey otherwise — so the run of solid line shows how far the student has got.
   */
  connectorDone(milestone: any): boolean {
    return !!milestone?.completed;
  }

  isLast(index: number): boolean {
    return index === (this.lessons?.length ?? 0) - 1;
  }

  /** Only shown when the course actually uses deadlines and the student has a start date. */
  get showsDeadlines(): boolean {
    return this.activateLessonDeadlineNotification && this.enrolledDate != '0001-01-01T00:00:00';
  }

  hasDeadline(milestone: any): boolean {
    return this.showsDeadlines && milestone?.milestonetimenumber > 0;
  }

  get completedPercent(): number {
    if (!this.lessons?.length) {
      return 0;
    }
    const done = this.lessons.filter(l => l.completed).length;
    return Math.round((done / this.lessons.length) * 100);
  }

  get completedCount(): number {
    return this.lessons?.filter(l => l.completed).length ?? 0;
  }

  srcrollToLastCompletedLesson(){
    const container = document.getElementById("timeline");
    if (!container || !this.lessons?.length) {
      return;
    }
    const completed = this.lessons.filter(l => l.completed).map(l => l.classposition);
    if (!completed.length) {
      return;
    }
    const item = document.getElementById(`${Math.max(...completed)}`);
    if (!item) {
      return;
    }
    container.scrollLeft = item.offsetLeft - container.offsetLeft;
  }

  setDate(milestone: any): Date | null {
    const timeVal: number = milestone.milestonetimenumber;
    const timeUnit: number = milestone.milestonetimeunit;
    if (timeVal > 0) {
      const date = milestone.usefixeddeadline ? new Date(milestone.publishdate) : new Date(this.enrolledDate);
      return timeUnit == 0 ? new Date(date.setHours(date.getHours() + timeVal)) : new Date(date.setDate(date.getDate() + timeVal));
    }
    return null;
  }

  deadlinePassed(milestone: any): boolean{
    const deadline = this.setDate(milestone);
    // no deadline set means it can never be overdue
    return deadline !== null && new Date() > deadline;
  }
}
